import { Fraction } from '@learnforge/demo-math/numeric';
import { gcd } from './factorisation';

export type Ratio = readonly number[];

export function simplifyRatio(r: Ratio): Ratio {
  if (r.length === 0) return [];
  let g = Math.abs(r[0] ?? 0);
  for (let i = 1; i < r.length; i += 1) {
    g = gcd(g, Math.abs(r[i] ?? 0));
    if (g === 1) break;
  }
  if (g === 0) return r.slice();
  return r.map((part) => part / g);
}

export function equivalentRatios(r: Ratio, count: number): Ratio[] {
  const simplified = simplifyRatio(r);
  const out: Ratio[] = [];
  for (let k = 1; k <= count; k += 1) {
    out.push(simplified.map((part) => part * k));
  }
  return out;
}

export function scaleByFactor(v: number, factor: Fraction): number {
  return (v * factor.num) / factor.den;
}

export function scaleRatioByFactor(r: Ratio, factor: Fraction): Ratio {
  return r.map((part) => scaleByFactor(part, factor));
}

export function harmonicMean(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sumReciprocals = values.reduce((s, v) => s + 1 / v, 0);
  // Round to 10 dp: the raw float for e.g. [60,40] is 47.99999999999999, which
  // would fail the spec's `toBe(48)` assertion. FP cleanup, not a value change.
  const result = values.length / sumReciprocals;
  return Math.round(result * 1e10) / 1e10;
}

export function ratioToFractions(r: Ratio): Fraction[] {
  const total = r.reduce((s, part) => s + part, 0);
  if (total === 0) return r.map(() => Fraction.ZERO);
  return r.map((part) => new Fraction(part, total));
}
